import { Link } from 'react-router-dom';
import { CheckIcon, GiftIcon } from 'lucide-react';
import { MediaView } from '../shared/MediaView';
import { ProductImage } from '../../ui/ProductImage';
import { useCatalog } from '../../contexts/CatalogContext';
import { useCartActions } from '../../contexts/StoreContext';
import { usePages, useSite } from '../../contexts/SiteContext';
import { unlockedIn } from '../../data/gifts';
import { shopLabels } from '../../data/shopLabels';
import { founder, salon } from '../../data/content';
import { euro } from '../../core/format/currency';
import { countSoldOut, isAllSoldOut, isSoldOut } from '../../core/rules/stock';
import { fill } from '../../utils/copy';
import { sectionHeading } from '../../ui/sections';
import { btnPrice, btnPriceAmount, btnPriceLabel } from '../../ui/styles';
import type { Routine } from '../../core/types/catalog';
const primary = btnPrice;

/**
 * Het anker van de pagina: het complete protocol, op blush. Bewust geen protocolkaart in een
 * kader (dan stond het verhaal er twee keer), maar één uitgeschreven blok: het beeld blijft staan
 * (computer), rechts het verhaal, de producten in volgorde van gebruik, wat erbij zit en de
 * cadeaus die je met dit protocol vrijspeelt. Eén knop, met de prijs erin.
 */
export function RoutineAnchor({
  routine
}: {
  routine: Routine;
}) {
  const {
    routineTotals
  } = useCatalog();
  const {
    addRoutine
  } = useCartActions();
  const {
    items,
    price,
    months
  } = routineTotals(routine);
  const site = useSite();
  const pages = usePages();
  const gifts = unlockedIn(site.giftTiers, site.policy.freeShippingFrom, price);
  const soldOutCount = countSoldOut(items);
  const allSoldOut = isAllSoldOut(items);
  return <section className="bg-blush py-20 sm:py-24 lg:py-32" aria-labelledby="anchor-routine" data-id="element-8273" data-mp="1j53jhnqo478w">
      <div className="mx-auto grid max-w-content gap-10 px-6 lg:grid-cols-12 lg:gap-16 lg:px-10" data-id="element-8274" data-mp="3pva3g9nkme78">
        <div className="lg:col-span-5" data-id="element-8275" data-mp="3kw6xmmzro3y7">
          <div className="lg:sticky lg:top-28" data-id="element-8276" data-mp="3kx8jlmibmaup">
            <MediaView media={{
            image: routine.image || founder.withProducts,
            alt: ''
          }} sizes="(min-width: 1024px) 38vw, 100vw" className="aspect-[4/3] w-full sm:aspect-[16/10] lg:aspect-[4/5]" data-id="element-8277" data-mp="3kyjnrnfohkmc" />
          </div>
        </div>

        <div className="lg:col-span-7" data-id="element-8278" data-mp="1xt8pxayy7jvs">
          <p className="text-[14px] text-ink" data-id="element-8279" data-mp="1xs75ifw1ll1e">{fill(pages.routines.anchorEyebrow)}</p>
          <h2 id="anchor-routine" className={`mt-4 ${sectionHeading} text-ink`} data-id="element-8280" data-mp="22tmq66ub5pj9">
            {routine.name}
          </h2>
          <p className="mt-6 max-w-xl text-[17px] leading-relaxed text-ink" data-id="element-8281" data-mp="27ro98rrqhouq">{routine.story}</p>
          {routine.forWho && <p className="mt-3 max-w-xl text-[15px] leading-relaxed text-muted" data-id="element-8282" data-mp="2coocyw142fio">Voor wie: {routine.forWho.toLowerCase()}.</p>}

          {/* De volgorde is de boodschap: zo gebruik je het, van reinigen tot bescherming. */}
          <h3 className="mt-10 text-[14px] text-ink" data-id="element-8283" data-mp="2qio429lwtsvx">In volgorde van gebruik</h3>
          <ol className="mt-3 divide-y divide-ink/15 border-y border-ink/15" data-id="element-8284" data-mp="2qgl34uj7yfcf">
            {items.map((p, i) => <li key={p.id} className="flex items-center gap-4 py-3" data-id="element-8285" data-mp="35l60nh9cex81" data-mpi={i}>
                <span aria-hidden="true" className="w-5 shrink-0 font-display text-lg text-muted tabular-nums" data-id="element-8286" data-mp="35jcrnvb9c0wv">
                  {i + 1}
                </span>
                <ProductImage src={p.image} alt="" width={48} className="h-14 w-12 shrink-0 bg-shell p-1" data-id="element-8287" data-mp="2vob5978d9cxz" />
                <div className="min-w-0 flex-1" data-id="element-8288" data-mp="2rmziraksh7u0">
                  <p className="text-[12px] text-ink" data-id="element-8289" data-mp="3obxdwtwgb7rp">{isSoldOut(p) ? shopLabels.soldOutShort : p.step}</p>
                  <Link to={`/webshop/${p.id}`} className="text-[15px] leading-snug text-ink underline-offset-4 hover:underline" data-id="element-8290" data-mp="3jd44bckac0wt">
                    {p.name}
                  </Link>
                </div>
                <span className={`text-[14px] tabular-nums text-muted ${isSoldOut(p) ? 'line-through' : ''}`} data-id="element-8291" data-mp="3al23hd6b0im0">{euro(p.price)}</span>
              </li>)}
          </ol>

          <div className="mt-8 grid gap-8 sm:grid-cols-2" data-id="element-8292" data-mp="66c21im74z54">
            {routine.included.length > 0 && <div data-id="element-8293" data-mp="3mtyiuff4vt7w">
                <h3 className="text-[14px] text-ink" data-id="element-8294" data-mp="3mtfbfsn40n84">Kosteloos inbegrepen</h3>
                <ul className="mt-3 space-y-2" data-id="element-8295" data-mp="3rsi7inl43j3y">
                  {routine.included.map(inc => <li key={inc} className="flex items-start gap-2.5 text-[15px] leading-snug text-ink" data-id="element-8296" data-mp="3dta5lmxkdww2">
                      <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-sage" strokeWidth={2} aria-hidden="true" data-id="element-8297" data-mp="3hu2dabqsftq8" />
                      {inc}
                    </li>)}
                </ul>
              </div>}
            {gifts.length > 0 && <div data-id="element-8298" data-mp="mgrs1q7c1bz">
                <h3 className="text-[14px] text-ink" data-id="element-8299" data-mp="n0a3jjrkrqj">Daarbij ligt klaar</h3>
                <ul className="mt-3 space-y-2" data-id="element-8300" data-mp="4o2c2852fhw7">
                  {gifts.map(g => <li key={g.ruleId} className="flex items-start gap-2.5 text-[15px] leading-snug text-ink" data-id="element-8301" data-mp="4lpkd39i9t5y">
                      <GiftIcon className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.5} aria-hidden="true" data-id="element-8302" data-mp="x4czm21tmdz4" />
                      {g.title}
                    </li>)}
                </ul>
              </div>}
          </div>

          <div className="mt-10 max-w-md" data-id="element-8303" data-mp="2cuxhsfa9qsm2">
            <p className="mb-3 text-[14px] text-muted" data-id="element-8304" data-mp="2rklipfejfp99">
              Gaat {months} mee · dezelfde prijs als los, zonder giswerk
            </p>
            {allSoldOut ? <a href={salon.whatsapp} target="_blank" rel="noreferrer" className={`${primary} justify-center`} data-id="element-8305" data-mp="2mma6iy0rt2nv">
                {shopLabels.soldOutShort} · vraag {founder.firstName} om een alternatief
                <span className="sr-only" data-id="element-8306" data-mp="1tyxlisa9d2wo">{shopLabels.viaWhatsApp}</span>
              </a> : <button type="button" onClick={() => addRoutine(routine.id)} className={primary} data-id="element-8307" data-mp="28s0vadhlyccu">
                <span className={btnPriceLabel} data-id="element-8308" data-mp="23x3nf9w2e91j">
                  Protocol in de mand<span className="sr-only" data-id="element-8309" data-mp="3k7xrmmrrzyk3">: {routine.name}</span>
                </span>
                <span className={btnPriceAmount} data-id="element-8310" data-mp="3f94h937scno5">{euro(price)}</span>
              </button>}
            {soldOutCount > 0 && !allSoldOut && <p className="mt-3 text-[13px] leading-relaxed text-muted" data-id="element-8311" data-mp="3mfgqbbn82duc">{shopLabels.partlySoldOut(soldOutCount)}</p>}
          </div>
        </div>
      </div>
    </section>;
}